/**
 * DA narration (tse:da_narration_* events, server/app/contracts/tse_da_narration.schema.json) as pure
 * functions over a small tracking record, so the socket hook and the replay walk share one set of rules.
 *
 * Rules from the contract and the owner:
 *   - start opens one copilot bubble per message_id WITH its source cards (V4 card shape);
 *   - deltas are append-only by index: duplicates are ignored, early ones wait for the gap to fill;
 *   - end text is used when nothing was visible yet or when it only extends what was shown; otherwise the
 *     shown text stays (it is never replaced) and only the image links missing from it are appended;
 *   - abort keeps whole sentences only; a bubble with nothing complete is removed.
 */

export const DA_NARRATION_BLOCK = "da_narration";

// Image lines the server appends after the narration: "[View image — caption](proxy url)".
const IMAGE_LINE_RE = /^\[View image[^\]]*\]\([^)]+\)\s*$/;
// A line is complete when it ends a sentence, a citation pill, a link or bold label.
const COMPLETE_END_RE = /(?:[.!?…:]|\]|\)|\*\*)["'”’)\]]*$/;
// Sentence ends inside a run: terminator (optionally closing bold / quote) then whitespace.
const SENTENCE_END_RE = /[.!?…](?:\*\*)?["'”’)]*(?:\s*\[\s*S\d+(?:\s*,\s*S\d+)*\s*\])*(?=\s)/g;
const EXCESS_BLANK_LINES_RE = /\n{3,}/g;

const normalise = (text) => String(text || "").replace(EXCESS_BLANK_LINES_RE, "\n\n").trim();

/** Source cards for SourceCards: the contract already uses the V4 shape; `cited` follows end.citations. */
export function narrationSources(sources, citations) {
  const cited = Array.isArray(citations) ? new Set(citations.map((c) => String(c).toUpperCase())) : null;
  return (Array.isArray(sources) ? sources : []).map((s, i) => ({
    ...s,
    n: s?.n || i + 1,
    cited: cited ? cited.has(String(s?.id || "").toUpperCase()) : !!s?.cited,
  }));
}

/** Text cut back to its last whole sentence (or whole line); "" when nothing complete was shown. */
export function completeSentences(text) {
  const t = String(text || "").replace(/\s+$/, "");
  if (!t) return "";
  if (COMPLETE_END_RE.test(t)) return t;
  let cut = -1;
  for (const m of t.matchAll(SENTENCE_END_RE)) cut = m.index + m[0].length;
  const lastBreak = t.lastIndexOf("\n");
  if (lastBreak > cut && COMPLETE_END_RE.test(t.slice(0, lastBreak).replace(/\s+$/, ""))) cut = lastBreak;
  return cut > 0 ? t.slice(0, cut).replace(/\s+$/, "") : "";
}

/** New tracking record for a start payload. */
export function startTrack(payload) {
  return {
    id: payload.message_id,
    diagnosisId: payload.diagnosis_id || "",
    seq: payload.seq ?? null,
    kind: payload.kind || "",
    replay: !!payload.replay,
    nextIndex: 0,
    pending: {},
    text: "",
    done: false,
  };
}

/** The bubble a start payload opens (sources above it, typing cursor until the first sentence). */
export function startMessage(payload, track) {
  return {
    id: track.id,
    sender: "copilot",
    text: "",
    sources: narrationSources(payload.sources),
    images: Array.isArray(payload.images) ? payload.images : [],
    isStreaming: !track.replay,
    isDone: false,
    daNarration: { diagnosisId: track.diagnosisId, seq: track.seq, kind: track.kind },
  };
}

/**
 * Applies one delta; returns {track, changed}. Index below nextIndex = duplicate (ignored); above = held
 * until the missing ones arrive; the held run is drained in order.
 */
export function applyDelta(track, payload) {
  const index = Number(payload?.index);
  const piece = typeof payload?.text === "string" ? payload.text : "";
  if (track.done || !Number.isInteger(index) || index < track.nextIndex || !piece) return { track, changed: false };
  if (index > track.nextIndex) {
    if (track.pending[index] !== undefined) return { track, changed: false };
    return { track: { ...track, pending: { ...track.pending, [index]: piece } }, changed: false };
  }
  const pending = { ...track.pending };
  let text = track.text + piece;
  let next = index + 1;
  while (pending[next] !== undefined) {
    text += pending[next];
    delete pending[next];
    next += 1;
  }
  return { track: { ...track, text, nextIndex: next, pending }, changed: true };
}

/** Final text for an end payload, given what is on screen. */
export function finalText(shown, endText) {
  const seen = normalise(shown);
  const final = normalise(endText);
  if (!seen) return final;
  if (!final || final === seen) return seen;
  if (final.startsWith(seen)) return final;
  // Shown text is never replaced: only the image links it lacks are appended.
  const missing = final.split("\n").filter((line) => IMAGE_LINE_RE.test(line.trim()) && !seen.includes(line.trim()));
  return missing.length ? `${seen}\n\n${missing.map((l) => l.trim()).join("\n")}` : seen;
}

/** Message patch for an end payload. */
export function endPatch(track, payload, sources) {
  return {
    text: finalText(track.text, payload?.text),
    sources: narrationSources(sources, payload?.citations),
    isStreaming: false,
    isDone: true,
    daNarration: {
      diagnosisId: track.diagnosisId, seq: track.seq, kind: track.kind,
      fallback: !!payload?.fallback, reason: payload?.reason || "none",
    },
  };
}

/** Message patch for an abort, or null when nothing complete was shown (the bubble is removed). */
export function abortPatch(track, reason) {
  const text = completeSentences(track.text);
  if (!text) return null;
  return {
    text,
    isStreaming: false,
    isDone: true,
    daNarration: { diagnosisId: track.diagnosisId, seq: track.seq, kind: track.kind, aborted: true, reason: reason || "" },
  };
}

/** Transcript row for a recorded da_narration replay block; null when it holds no text. */
export function replayNarrationMessage(block, timestamp) {
  if (!block || typeof block !== "object") return null;
  const raw = block.aborted ? completeSentences(block.text) : normalise(block.text);
  if (!raw) return null;
  return {
    id: block.message_id,
    sender: "copilot",
    text: raw,
    sources: narrationSources(block.sources, block.citations),
    images: Array.isArray(block.images) ? block.images : [],
    isStreaming: false,
    isDone: true,
    timestamp,
    daNarration: {
      diagnosisId: block.diagnosis_id || "", seq: block.seq ?? null, kind: block.kind || "",
      fallback: !!block.fallback, reason: block.reason || "none", aborted: !!block.aborted,
    },
  };
}
